import React from 'react';
import I18n from 'i18n-js';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { CoreBumpRecommendationListingCard } from '@reverbdotcom/commons/src/gql/graphql';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { formatBid, getCompetitorCount, getFeedbackState, getInsight, isBrandNew } from './bump_helpers';
import { RCAccordion, RCAlertBox, RCCallout, RCIcon, RCThemedBox } from '@reverbdotcom/cadence/components';
import { withPrefix } from '@reverbdotcom/commons/src/i18n_helpers';
import { REVERB_BUMP, useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCText, RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';
import { LightningIcon, TrophyIcon } from '@phosphor-icons/react';
import { RecommendedRadioV4 } from './recommended_radio_v4';
import { useEppoExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';

const COMPONENT_NAME = 'BumpModalBodyTopPickIteration';

interface Props {
  bid: number;
  handleBidChange: (bid: number) => void;
  listing: CoreBumpRecommendationListingCard.Fragment;
  recommendationAmount: number;
  selectedBid: number;
  successMessage: string;
  failMessage: string;
  hideSubmit?: boolean;
  onSubmit: () => void;
  setShowCustomWarning: React.Dispatch<React.SetStateAction<boolean>>;
  showCustomWarning: boolean;
  minimumBumpBid?: number;
  showTopPickVariant?: boolean;
}
const t = withPrefix('discovery.bumpModal');

const BUMP_HELP_CENTER_ARTICLE_ID = '360007185794';
const SELL_THROUGH_PERCENT = '25';

export function BumpModalBodyTopPickIteration(props: Props) {
  const mParticleViewData = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ViewedBumpSlider,
    listing: props.listing,
    startingBumpRatePercent: formatBid(props.bid),
    bumpRecommendationPercent: props.recommendationAmount,
  };

  useViewTracking(mParticleViewData, true);

  const competingCount = getCompetitorCount(props.listing);
  const cspPath = props.listing?.csp?.webLink?.path;
  const showOfferBumpFeeDisclaimer = useEppoExpEnabled({ flagKey: eppoFlags.CLOSE_OFFER_BUMP_LOOPHOLE });

  return (
    <div className="bump-modal-top-pick-iteration">
      {props.failMessage &&
        <div className="scaling-mb-4">
          <RCAlertBox type="error">
            <SanitizedRender html={props.failMessage} />
          </RCAlertBox>
        </div>
      }
      {props.successMessage &&
        <div className="scaling-mb-4">
          <RCAlertBox type="success">
            {props.successMessage}
          </RCAlertBox>
        </div>
      }
      <div className="bump-modal-top-pick-iteration__header">
        {props.showTopPickVariant && <RCIcon icon={<LightningIcon weight="duotone" />} size={20} />}
        <div>
          <Translate text="discovery.bumpModal.overviewIteration">
            <b className="fg-color-text-accent"><Translate text="discovery.bumpModal.reverbBump" /></b>
          </Translate>
          {props.showTopPickVariant && (
            <>
              {' '}
              <Translate text="discovery.bumpModal.overviewTail">
                <b className="weight-semibold"><Translate text="discovery.bumpModal.sellThroughCallout" args={{ percent: SELL_THROUGH_PERCENT }} /></b>
              </Translate>
            </>
          )}
          {' '}
          <Translate text="discovery.bumpModal.sellThroughCalloutTail" />
        </div>
      </div>
      <div className="d-flex fx-dir-col gap-2">
        <div>
          <RCTextUtility size="400" weight="bold" >
            {t('setRateIteration')}
          </RCTextUtility>
          {!props.showTopPickVariant && (
            <RCTextUtility size="400" color="secondary">
              {t('setRateHelp')}
            </RCTextUtility>
          )}
        </div>
        <RecommendedRadioV4
          currentBid={props.bid}
          handleBidChange={props.handleBidChange}
          recommendationAmount={props.recommendationAmount}
          showCustomWarning={props.showCustomWarning}
          setShowCustomWarning={props.setShowCustomWarning}
          minimumBumpBid={props.minimumBumpBid}
          showTopPickVariant={props.showTopPickVariant}
          insight={getInsight(props.listing)}
        />
      </div>
      {props.showTopPickVariant && <FeedbackBox
        competingCount={competingCount}
        cspPath={cspPath}
        currentBidPercent={formatBid(props.bid)}
        recommendedBidPercent={props.recommendationAmount}
        selectedBidPercent={formatBid(props.selectedBid)}
        listing={props.listing}
      />}
      <div className="bump-modal__how-it-works-iteration">
        <RCThemedBox themeType="utility" variant="neutral-ultralight" className="plr-5 mobile-plr-4 ptb-2 bd-radius-md">
          <RCAccordion id="bump-how-it-works" size="small" heading={t('howItWorks')}>
            <p>{t('bumpDescription')}</p>
            <ol className="list-type-numeric">
              <li>
                <Translate text="discovery.bumpModal.bumpStep1">
                  <CoreLink target="_blank" to={useHelpCenterArticleUrl(BUMP_HELP_CENTER_ARTICLE_ID)}>
                    <Translate text="discovery.bumpModal.bumpStep1Link" />
                  </CoreLink>
                </Translate>
              </li>
              <li>
                <div>{t('bumpStep2')}</div>
                <div className="pl-1">
                  <ul className="list-type-disc">
                    <li>{t('bumpExample1')}</li>
                    <li>{t('bumpExample2')}</li>
                    <li>{t('bumpExample3')}</li>
                    <li>{t('bumpExample4')}</li>
                  </ul>
                </div>
              </li>
              <li>
                {t('bumpStep3')}
              </li>
            </ol>
            <div className="mt-3">
              <a target="_blank" rel="noreferrer" href={REVERB_BUMP}>
                {t('readMore')}
              </a>
            </div>
          </RCAccordion>
        </RCThemedBox>
      </div>
      <div className="d-flex fx-dir-col fg-color-text-secondary gap-1">
        <RCText.Fine size="300">
          <Translate text="discovery.bumpModal.footnote">
            <b>
              {t('bumpSellConditionCallout')}
            </b>
          </Translate>
        </RCText.Fine>
        {showOfferBumpFeeDisclaimer && <RCText.Fine size="300">
          <Translate text="discovery.bumpModal.offerBumpFeeDisclaimer" />
        </RCText.Fine>}
        {props.showTopPickVariant && <RCText.Fine size="300">
          <Translate text="discovery.bumpModal.topPickDisclaimer" />
        </RCText.Fine>}
      </div>

      {
        !props.hideSubmit && (
          <div className="align-right scaling-mt-8">
            <button
              type="button"
              className="button button--primary size-90 weight-bold"
              disabled={props.showCustomWarning}
              onClick={props.onSubmit}
            >
              {t('save')}
            </button>
          </div>
        )
      }
    </div>
  );
}

function FeedbackBox({
  competingCount = 0,
  cspPath,
  currentBidPercent,
  recommendedBidPercent,
  selectedBidPercent,
  listing,
}: {
  competingCount?: number;
  cspPath?: string | null;
  currentBidPercent: number;
  recommendedBidPercent: number;
  selectedBidPercent: number;
  listing: CoreBumpRecommendationListingCard.Fragment;
}) {
  const { key: feedbackStateKey, winner, overbid } = getFeedbackState({
    currentBidPercent,
    selectedBidPercent,
    recommendedBidPercent,
    isCurrentFeaturedWinner: getInsight(listing)?.featuredWinner,
  });

  const conditionTranslationKey = isBrandNew(listing.condition?.conditionSlug) ? 'discovery.bumpModal.brandNew' : 'discovery.bumpModal.used';

  const topPickCallout = (
    competingCount > 0 ? (
      <Translate
        text={`discovery.bumpModal.feedbackDetails.${feedbackStateKey}`}
        args={{
          count: String(competingCount),
          condition: I18n.t(conditionTranslationKey),
          itemWord: I18n.t(
            competingCount === 1
              ? 'discovery.bumpModal.topPickCalloutItemSingular'
              : 'discovery.bumpModal.topPickCalloutItemPlural',
          ),
        }}
      >
        <b>
          <Translate text={`discovery.bumpModal.feedbackCallout.${feedbackStateKey}`} />
        </b>
      </Translate>
    ) : (
      <Translate
        text={`discovery.bumpModal.feedbackDetails.noCount.${feedbackStateKey}`}
        args={{
          condition: I18n.t(conditionTranslationKey),
        }}
      >
        <b>
          <Translate text={`discovery.bumpModal.feedbackCallout.${feedbackStateKey}`} />
        </b>
      </Translate>
    )
  );

  return (
    <div className="bump-modal-top-pick-iteration__feedback-box">
      <RCCallout
        themeType="utility"
        variant={winner && !overbid ? 'success-ultralight' : 'neutral-ultralight'}
        border="thin"
        icon={winner ? <TrophyIcon weight="duotone" className="fg-color-accent-success" /> : undefined}
        themedIcon={false}
      >
        <RCText.Default size="400">
          {topPickCallout}
        </RCText.Default>
        {!!cspPath && (
          <CoreLink to={cspPath} target="_blank" rel="noreferrer" className="text-link">
            <RCText.Default size="400">{t('topPickViewProductPage')}</RCText.Default>
          </CoreLink>
        )}
      </RCCallout>
    </div>
  );
}
